import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { API_ORIGIN } from '../../../services/apiBase';
import { COLOR } from '../../../components/ss/tokens';

const CATEGORIES = ['General', 'Team', 'Care', 'Events', 'Facilities', 'Community'];

function UploadZone({ onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('General');
  const inputRef = useRef();

  const doUpload = async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('caption', caption);
    form.append('category', category);
    setUploading(true);
    try {
      const res = await api.post('/media/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.data);
      setCaption('');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    if (!files?.length) return;
    Array.from(files).forEach(f => doUpload(f));
  };

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          placeholder="Caption (optional)"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          style={inputStyle}
        />
        <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? COLOR.primary : COLOR.grayBorder}`,
          borderRadius: 12, padding: '40px 24px',
          textAlign: 'center', cursor: 'pointer',
          background: dragging ? COLOR.blueBg : COLOR.bgMarketing,
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>📁</div>
        <p style={{ margin: 0, color: COLOR.textMuted, fontSize: 14 }}>
          {uploading ? 'Uploading…' : 'Drop photos or videos here, or click to browse'}
        </p>
        <p style={{ margin: '4px 0 0', color: COLOR.textFaint, fontSize: 12 }}>
          JPG, PNG, GIF, WEBP, MP4, WEBM — max 50MB
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
}

function MediaCard({ item, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(item.caption || '');
  const [category, setCategory] = useState(item.category || 'General');
  const [saving, setSaving] = useState(false);
  const API_BASE = API_ORIGIN;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/media/${item.media_id}`, { caption, category });
      onUpdate(res.data.data);
      setEditing(false);
    } catch (e) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    const res = await api.patch(`/media/${item.media_id}`, { is_active: !item.is_active });
    onUpdate(res.data.data);
  };

  const del = async () => {
    if (!window.confirm('Delete this media item permanently?')) return;
    await api.delete(`/media/${item.media_id}`);
    onDelete(item.media_id);
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 12, overflow: 'hidden',
      border: `1px solid ${COLOR.border}`,
      opacity: item.is_active ? 1 : 0.5,
    }}>
      <div style={{ position: 'relative', aspectRatio: '16/9', background: COLOR.navy }}>
        {item.media_type === 'VIDEO' ? (
          <video
            src={`${API_BASE}${item.url}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <img
            src={`${API_BASE}${item.url}`}
            alt={item.caption}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <span style={{
          position: 'absolute', top: 8, right: 8,
          background: item.media_type === 'VIDEO' ? COLOR.purple : COLOR.primary,
          color: '#fff', fontSize: 10, padding: '2px 8px',
          borderRadius: 4, textTransform: 'uppercase',
        }}>{item.media_type}</span>
        {!item.is_active && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)', color: '#fff', fontWeight: 700, fontSize: 13,
          }}>HIDDEN</div>
        )}
      </div>

      <div style={{ padding: '12px 14px' }}>
        {editing ? (
          <>
            <input
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Caption"
              style={{ ...inputStyle, marginBottom: 8, width: '100%', boxSizing: 'border-box' }}
            />
            <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, marginBottom: 8, width: '100%', boxSizing: 'border-box' }}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setEditing(false)} style={btnSecondary}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 4px', fontSize: 13, color: COLOR.text, fontWeight: 500 }}>
              {item.caption || <span style={{ color: COLOR.textFaint }}>No caption</span>}
            </p>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: COLOR.textMuted }}>{item.category}</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setEditing(true)} style={btnSecondary}>Edit</button>
              <button onClick={toggle} style={btnSecondary}>
                {item.is_active ? 'Hide' : 'Show'}
              </button>
              <button onClick={del} style={{ ...btnSecondary, color: COLOR.red, borderColor: COLOR.redBorder }}>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function MediaManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  const load = () => {
    api.get('/media/all')
      .then(r => setItems(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUploaded = (item) => setItems(prev => [item, ...prev]);
  const handleUpdate = (updated) => setItems(prev => prev.map(m => m.media_id === updated.media_id ? updated : m));
  const handleDelete = (id) => setItems(prev => prev.filter(m => m.media_id !== id));

  const cats = ['All', ...new Set(items.map(m => m.category).filter(Boolean))];
  const visible = filter === 'All' ? items : items.filter(m => m.category === filter);

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: COLOR.text, margin: '0 0 4px' }}>
          Media Library
        </h2>
        <p style={{ color: COLOR.textMuted, fontSize: 14, margin: 0 }}>
          Upload photos and videos that appear in the public gallery.
        </p>
      </div>

      <UploadZone onUploaded={handleUploaded} />

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{
            padding: '5px 16px', borderRadius: 100,
            border: '2px solid',
            borderColor: filter === c ? COLOR.primary : COLOR.border,
            background: filter === c ? COLOR.primary : '#fff',
            color: filter === c ? '#fff' : COLOR.textMuted,
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
          }}>{c} {c === 'All' ? `(${items.length})` : `(${items.filter(m => m.category === c).length})`}</button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: COLOR.textFaint }}>Loading media…</p>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: COLOR.textFaint }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🖼️</div>
          <p>No media yet. Upload photos and videos above.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 20,
        }}>
          {visible.map(item => (
            <MediaCard
              key={item.media_id}
              item={item}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  padding: '8px 12px', borderRadius: 7,
  border: `1px solid ${COLOR.border}`, fontSize: 13,
  fontFamily: 'Inter, sans-serif', outline: 'none', color: COLOR.text,
  background: '#fff',
};
const btnPrimary = {
  padding: '6px 14px', borderRadius: 6,
  background: COLOR.primary, color: '#fff', border: 'none',
  fontWeight: 600, fontSize: 12, cursor: 'pointer',
};
const btnSecondary = {
  padding: '6px 14px', borderRadius: 6,
  background: '#fff', color: COLOR.textMuted,
  border: `1px solid ${COLOR.border}`,
  fontWeight: 600, fontSize: 12, cursor: 'pointer',
};
